import { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useMatch, useNavigate, useParams } from 'react-router-dom';

import { getSelectedCategoryIdToFetch } from 'entities/Category/model/selectors/categorySelectors';
import {
    getSelectedPropertyCatalogId,
    getSelectedPropertyId,
} from 'entities/Property/model/selectors/PropertySelectors';
import { getAddCatalogItemMode } from 'features/AddCatalogItem/model/selectors/addCatalogItemSelectors';
import {
    getSearchCatalogItemIsActive,
    getSearchLocalCatalogItemValue,
} from 'features/SearchCatalogItems/model/selectors/searchCatalogItemsSelectors';
import {
    getRouteForbidden,
    getRoutePropertiesCatalogInventory,
} from 'shared/const/router';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { useInitialEffect } from 'shared/lib/hooks/useInitialEffect';
import { isArray, isObject } from 'shared/lib/lodash/lodash';
import { fetchInventoryItems } from 'widgets/InventoryTable/model/services/fetchCatalogItems/fetchInventoryItems';

import { fetchCatalogItems } from '../model/services/fetchCatalogItems/fetchCatalogItems';
import { fetchGlobalCatalogItems } from '../model/services/fetchGlobalCatalogItems/fetchGlobalCatalogItems';
import { catalogItemActions } from '../model/slices/CatalogItemSlice';
import {
    CatalogItem,
    GlobalCatalogItem,
} from '../model/types/CatalogItemSchema';

export const useFetchCatalogItems = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const params = useParams();

    const selectedPropertyCatalogId = useSelector(getSelectedPropertyCatalogId);
    const selectedCategoryId = useSelector(getSelectedCategoryIdToFetch);
    const selectedPropertyId = useSelector(getSelectedPropertyId);

    const isActiveSearch = useSelector(getSearchCatalogItemIsActive);
    const localSearchValue = useSelector(getSearchLocalCatalogItemValue);
    const isEditingCatalog = useSelector(getAddCatalogItemMode);
    const isPropertyInventoryCatalogRoute = Boolean(
        useMatch(getRoutePropertiesCatalogInventory(':propertyId') + '/*'),
    );

    const initialCatalogItemIdParam = useRef(null);
    const navigateRef = useRef(null);
    const initialLoaded = useRef(false);

    useInitialEffect(() => {
        initialCatalogItemIdParam.current = params.itemId;
        navigateRef.current = navigate;
    });

    const onInitialLoad = useCallback(
        (items: CatalogItem[] | GlobalCatalogItem[]) => {
            const itemId = initialCatalogItemIdParam.current;
            const navigate = navigateRef.current;

            if (itemId && isArray(items)) {
                let foundId = null;

                for (const item of items) {
                    if ('id' in item && item.id === itemId) {
                        foundId = item.id;
                        break;
                    }

                    if ('uid' in item && item.uid === itemId) {
                        foundId = item.uid;
                        break;
                    }
                }

                initialLoaded.current = true;

                if (foundId) {
                    dispatch(catalogItemActions.setSelectedItemById(foundId));
                    dispatch(catalogItemActions.setShowDetailedView(true));
                } else {
                    navigate(getRouteForbidden());
                }
            }
        },
        [dispatch],
    );

    const onInitialLoadInventory = useCallback(
        (items: CatalogItem[]) => {
            const itemId = initialCatalogItemIdParam.current;
            const navigate = navigateRef.current;

            if (itemId && isArray(items)) {
                let foundItem = items.find((item) => item.id === itemId);

                initialLoaded.current = true;

                if (foundItem) {
                    dispatch(catalogItemActions.setSelectedItem(foundItem));
                    dispatch(catalogItemActions.setShowDetailedView(true));
                } else {
                    navigate(getRouteForbidden());
                }
            }
        },
        [dispatch],
    );

    const onFetchCatalogItems = useCallback(
        async (isGlobal: boolean) => {
            let response = null;
            let items = [];

            if (isGlobal) {
                response = await dispatch(
                    fetchGlobalCatalogItems({
                        categoryId: selectedCategoryId,
                        propertyId: selectedPropertyId,
                    }),
                );

                items =
                    isObject(response.payload) &&
                    'items' in response.payload &&
                    isArray(response.payload?.items)
                        ? response.payload.items
                        : [];
            } else {
                response = await dispatch(
                    fetchCatalogItems({
                        data: {
                            catalogId: selectedPropertyCatalogId,
                            categoryId: selectedCategoryId,
                        },
                    }),
                );
                items = isArray(response.payload) ? response.payload : [];
            }

            if (!response?.payload || initialLoaded.current) return;

            onInitialLoad(items);
        },
        [
            dispatch,
            onInitialLoad,
            selectedCategoryId,
            selectedPropertyCatalogId,
            selectedPropertyId,
        ],
    );

    const onFetchInventoryItems = useCallback(
        async (catalogId: string) => {
            const response = await dispatch(fetchInventoryItems(catalogId));
            const items = isArray(response.payload) ? response.payload : [];

            if (!response?.payload || initialLoaded.current) return;

            onInitialLoadInventory(items);
        },
        [dispatch, onInitialLoadInventory],
    );

    /**
     * Reloads whatever list the user is viewing: debounced search results when search is active,
     * otherwise the category-scoped catalog fetch (same split as `SearchCatalogItemsField` vs
     * category `useEffect` on the property catalog page).
     */
    const refreshVisiblePropertyCatalogList = useCallback(async () => {
        if (!selectedPropertyCatalogId) {
            return;
        }

        const trimmedSearch = localSearchValue?.trim() ?? '';
        if (isActiveSearch && trimmedSearch) {
            await dispatch(
                fetchCatalogItems({
                    data: {
                        catalogId: selectedPropertyCatalogId,
                        searchStr: trimmedSearch,
                        ...(isPropertyInventoryCatalogRoute && {
                            isInventory: true,
                        }),
                    },
                }),
            );
        } else if (selectedCategoryId) {
            await onFetchCatalogItems(isEditingCatalog);
        }
    }, [
        dispatch,
        isActiveSearch,
        isEditingCatalog,
        isPropertyInventoryCatalogRoute,
        localSearchValue,
        onFetchCatalogItems,
        selectedCategoryId,
        selectedPropertyCatalogId,
    ]);

    return {
        onFetchCatalogItems,
        onFetchInventoryItems,
        refreshVisiblePropertyCatalogList,
    };
};
